<!--
    💼 BoxComponent - Componente modular para containers de conteúdo

    📝 Modo de Uso:
    import BoxComponent from '@Component/boxs/BoxComponent.vue';

    🔧 Props Disponíveis:
    • title: String - Título principal do box
    • subTitle: String - Subtítulo descritivo
    • icon: String - Ícone para o header (usa sw-icon)
    • variant: String - Estilo visual (default, primary, success, warning, danger, info)
    • actions: Array - Array de ações interativas (veja tipos abaixo)
    • loading: Boolean - Exibe overlay de carregamento

    📱 Eventos Emitidos:
    • @action-click - Botões simples
    • @select-change - Mudança em selects
    • @input-change - Alteração em inputs
    • @input-enter - Enter pressionado em inputs
    • @toggle-change - Mudança em toggles
    • @button-group-click - Clique em grupos de botões

    🎯 Tipos de Actions:

    📋 Select Dropdown:
    {
        type: 'select',
        key: 'filter',
        selectedValue: 'all',
        selectedLabel: 'Todos',
        selectedIcon: 'filter',
        options: [
            { value: 'all', label: 'Todos', icon: 'filter' },
            { value: 'active', label: 'Ativos', icon: 'check' }
        ]
    }

    🔍 Input de Busca:
    {
        type: 'input',
        key: 'search',
        placeholder: 'Buscar...',
        inputType: 'text'
    }

    🔘 Toggle/Switch:
    {
        type: 'toggle',
        key: 'notifications',
        label: 'Notificações',
        checked: true
    }

    🎛️ Grupo de Botões:
    {
        type: 'button-group',
        key: 'view',
        buttons: [
            { icon: 'grid-view', key: 'grid', title: 'Grade', active: true },
            { icon: 'list-view', key: 'list', title: 'Lista' }
        ]
    }

    🔗 Botão Simples/Link:
    {
        type: 'button',
        icon: 'link',
        title: 'Ir para Marketing',
        click: 'goToMarketing',
        route: 'Marketing'
    }

    💡 Exemplo Completo:
    <BoxComponent
        title="Lista de Processos"
        sub-title="Todos os processos para Download"
        icon="process"
        variant="primary"
        :actions="[
            {
                type: 'select',
                key: 'filter',
                selectedValue: 'all',
                selectedLabel: 'Todos',
                selectedIcon: 'filter',
                options: [
                    { value: 'all', label: 'Todos', icon: 'filter' },
                    { value: 'active', label: 'Ativos', icon: 'check' }
                ]
            },
            {
                type: 'input',
                key: 'search',
                placeholder: 'Buscar...',
                inputType: 'text'
            },
            {
                type: 'toggle',
                key: 'notifications',
                label: 'Notificações',
                checked: true
            },
            {
                type: 'button-group',
                key: 'view',
                buttons: [
                    { icon: 'grid-view', key: 'grid', title: 'Grade', active: true },
                    { icon: 'list-view', key: 'list', title: 'Lista' }
                ]
            },
            {
                text: 'Ir para Marketing',
                type: 'button',
                icon: 'link',
                title: 'Ir para Marketing',
                route: 'Marketing'
            }
        ]"
        @action-click="handleAction"
        @select-change="handleSelectChange"
        @input-change="handleInputChange"
        @input-enter="handleInputEnter"
        @toggle-change="handleToggleChange"
        @button-group-click="handleButtonGroupClick"
    >
        <p>Conteúdo do box aqui</p>
    </BoxComponent>

    🎨 Variantes de Estilo:
    • default - Azul padrão
    • primary - Azul primário
    • success - Verde sucesso
    • warning - Laranja alerta
    • danger - Vermelho erro
    • info - Roxo informativo

    📚 Documentação: Consulte CLAUDE.md para padrões e convenções
 -->
<template>
    <div class="box" :class="`box--${variant}`">
        <!-- Header aprimorado -->
        <div class="box_header" v-if="title || subTitle">
            <div class="box_header_content">
                <div class="box_header_icon" v-if="icon">
                    <sw-icon :name="icon" />
                </div>
                <div class="box_header_text">
                    <h1 class="box_header_title">{{ title }}</h1>
                    <p class="box_header_subtitle" v-if="subTitle">{{ subTitle }}</p>
                </div>
            </div>

            <!-- Actions melhoradas -->
            <div class="box_header_actions" v-if="actions && actions.length">
                <div 
                    class="box_header_action"
                    v-for="(action, index) in actions" 
                    :key="index"
                    :title="action.title"
                    @click="handleActionClick(action)"
                >
                    <!-- Select personalizado para Box -->
                    <div v-if="action.type === 'select'" class="box_header_select">
                        <div
                            class="box_header_selectButton"
                            @click="toggleSelectDropdown(action.key)"
                            :class="{ 'box_header_selectButton--active': activeDropdown === action.key }"
                        >
                            <div class="box_header_selectContent">
                                <sw-icon
                                    :name="getSelectedIcon(action)"
                                    v-if="getSelectedIcon(action)"
                                    class="box_header_selectIcon"
                                />
                                <span class="box_header_selectText">{{ action.selectedLabel || action.options[0]?.label || 'Selecionar' }}</span>
                            </div>
                            <sw-icon name="chevron-down" :class="{ 'rotate-180': activeDropdown === action.key }" />
                        </div>

                        <div
                            v-if="activeDropdown === action.key"
                            class="box_header_selectDropdown"
                            @click.stop
                        >
                            <div
                                v-for="option in action.options"
                                :key="option.value"
                                class="box_header_selectOption"
                                :class="{ 'box_header_selectOption--selected': option.value === action.selectedValue }"
                                @click="selectOption(action, option)"
                            >
                                <sw-icon :name="option.icon" v-if="option.icon" />
                                <span>{{ option.label }}</span>
                            </div>
                        </div>
                    </div>

                    <!-- Input personalizado para Box -->
                    <div v-else-if="action.type === 'input'" class="box_header_input">
                        <input
                            :type="action.inputType || 'text'"
                            :placeholder="action.placeholder || ''"
                            :value="action.value || ''"
                            class="box_header_inputField"
                            @input="handleInputChange(action, $event)"
                            @keyup.enter="handleInputEnter(action, $event)"
                        />
                    </div>

                    <!-- Toggle/Switch para Box -->
                    <div v-else-if="action.type === 'toggle'" class="box_header_toggle">
                        <label class="box_header_toggleLabel">
                            <input
                                type="checkbox"
                                :checked="action.checked || false"
                                @change="handleToggleChange(action, $event)"
                                class="box_header_toggleInput"
                            />
                            <span class="box_header_toggleSlider"></span>
                            <span v-if="action.label" class="box_header_toggleText">{{ action.label }}</span>
                        </label>
                    </div>

                    <!-- Grupo de botões -->
                    <div v-else-if="action.type === 'button-group'" class="box_header_buttonGroup">
                        <button
                            v-for="button in action.buttons"
                            :key="button.key || button.icon"
                            class="box_header_actionButton"
                            :class="{ 'box_header_actionButton--active': button.active }"
                            :title="button.title"
                            @click="handleButtonGroupClick(action, button)"
                        >
                            <sw-icon :name="button.icon" />
                        </button>
                    </div>

                    <!-- Actions normais (botões simples) -->
                    <template v-else-if="!action.type || action.type === 'button'">
                        <router-link
                            v-if="action.route && action.click"
                            :to="{name: action.route}"
                            class="box_header_actionLink"
                        >
                            <div
                                class="box_header_actionButton"
                                :class="{ 'box_header_actionButton--withText': action.text }"
                                @click="handleActionClick(action)"
                            >
                                <sw-icon :name="action.icon" v-if="action.icon"/>
                                <span v-if="action.text" class="box_header_actionText">{{ action.text }}</span>
                            </div>
                        </router-link>

                        <router-link
                            v-else-if="action.route && !action.click"
                            :to="{name: action.route}"
                            class="box_header_actionLink"
                        >
                            <div
                                class="box_header_actionButton"
                                :class="{ 'box_header_actionButton--withText': action.text }"
                            >
                                <sw-icon :name="action.icon" v-if="action.icon"/>
                                <span v-if="action.text" class="box_header_actionText">{{ action.text }}</span>
                            </div>
                        </router-link>

                        <div
                            v-else
                            class="box_header_actionButton"
                            :class="{ 'box_header_actionButton--withText': action.text }"
                            @click="handleActionClick(action)"
                        >
                            <sw-icon :name="action.icon" v-if="action.icon"/>
                            <span v-if="action.text" class="box_header_actionText">{{ action.text }}</span>
                        </div>
                    </template>
                </div>
            </div>
        </div>

        <!-- Content melhorado -->
        <div class="box_content" :class="{ 'box_content--noHeader': !title && !subTitle }">
            <slot />
        </div>

        <!-- Loading overlay opcional -->
        <div class="box_loading" v-if="loading">
            <app-spinner />
        </div>
    </div>
</template>

<script setup>
    import { ref, onMounted, onUnmounted } from 'vue';
    import { BOX_VARIANTS } from '@/constants';

    const props = defineProps({
        title: String,
        subTitle: String,
        icon: String,
        variant: {
            type: String,
            default: BOX_VARIANTS.DEFAULT,
            validator: (value) => Object.values(BOX_VARIANTS).includes(value)
        },
        actions: Array,
        loading: {
            type: Boolean,
            default: false
        }
    });

    const emit = defineEmits(['action-click', 'select-change', 'input-change', 'input-enter', 'toggle-change', 'button-group-click']);

    const activeDropdown = ref(null);

    const handleActionClick = (action) => {
        // Verificar se action existe
        if (!action) return;

        // Para actions que não são select, input, toggle ou button-group
        if (!action.type || action.type === 'button') {
            emit('action-click', action);
        }
    };

    const toggleSelectDropdown = (key) => {
        activeDropdown.value = activeDropdown.value === key ? null : key;
    };

    const selectOption = (action, option) => {
        // Fechar dropdown
        activeDropdown.value = null;

        // Emitir evento de mudança - o componente pai que vai atualizar o estado
        emit('select-change', {
            key: action.key,
            value: option.value,
            label: option.label,
            icon: option.icon,
            option: option
        });
    };

    const getSelectedIcon = (action) => {
        if (action.selectedIcon) {
            return action.selectedIcon;
        }
        // Se não tem ícone selecionado, pega o do primeiro item ou do valor atual
        const currentOption = action.options?.find(opt => opt.value === action.selectedValue) || action.options?.[0];
        return currentOption?.icon;
    };

    // Handlers para novos tipos de actions
    const handleInputChange = (action, event) => {
        emit('input-change', {
            key: action.key,
            value: event.target.value,
            action: action
        });
    };

    const handleInputEnter = (action, event) => {
        emit('input-enter', {
            key: action.key,
            value: event.target.value,
            action: action
        });
    };

    const handleToggleChange = (action, event) => {
        emit('toggle-change', {
            key: action.key,
            checked: event.target.checked,
            action: action
        });
    };

    const handleButtonGroupClick = (action, button) => {
        emit('button-group-click', {
            key: action.key,
            button: button,
            action: action
        });
    };

    // Fechar dropdown ao clicar fora
    const closeDropdownOnClickOutside = (event) => {
        if (!event.target.closest('.box_header_select')) {
            activeDropdown.value = null;
        }
    };

    onMounted(() => {
        document.addEventListener('click', closeDropdownOnClickOutside);
    });

    onUnmounted(() => {
        document.removeEventListener('click', closeDropdownOnClickOutside);
    });
</script>

<style lang="scss" scoped>
/* =======================
   BOX COMPONENT MODERNO
   ======================= */

.box {
    position: relative;
    background: var(--backgroundCard, #ffffff);
    border-radius: 0.75em;
    box-shadow: 0 0.25em 1em rgba(0, 0, 0, 0.08),
                0 0.125em 0.5em rgba(0, 0, 0, 0.06);
    border: 1px solid rgba(255, 255, 255, 0.1);
    backdrop-filter: blur(10px);
    transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
    z-index: 10;
    
    &:hover {
        transform: translateY(-0.25em);
        box-shadow: 0 0.5em 2em rgba(0, 0, 0, 0.12), 
                    0 0.25em 1em rgba(0, 0, 0, 0.08);
    }

    // Variantes do box
    &--primary {
        .box_header_icon {
            background: linear-gradient(135deg, var(--colorBlue, #3b82f6), var(--colorBlueDark, #1d4ed8));
        }
    }

    &--success {
        .box_header_icon {
            background: linear-gradient(135deg, var(--colorGreen, #10b981), var(--colorGreenDark, #059669));
        }
    }

    &--warning {
        .box_header_icon {
            background: linear-gradient(135deg, var(--colorOrange, #f59e0b), var(--colorOrangeDark, #d97706));
        }
    }

    &--danger {
        .box_header_icon {
            background: linear-gradient(135deg, var(--colorRed, #ef4444), var(--colorRedDark, #dc2626));
        }
    }

    &--info {
        .box_header_icon {
            background: linear-gradient(135deg, var(--colorPurple, #8b5cf6), var(--colorPurpleDark, #7c3aed));
        }
    }
}

/* Header do Box */
.box_header {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 2em;
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.05));
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
    backdrop-filter: blur(10px);
    z-index: 2;
}

.box_header_content {
    display: flex;
    align-items: center;
    gap: 1.5em;
    flex: 1;
}

.box_header_icon {
    width: 2.75em;
    height: 2.75em;
    border-radius: 0.625em;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, var(--colorBlue, #3b82f6), var(--colorBlueDark, #1d4ed8));
    color: white;
    font-size: 1.125em;
    box-shadow: 0 0.375em 0.75em rgba(59, 130, 246, 0.25);
    transition: all 0.3s ease;

    &:hover {
        transform: scale(1.05);
        box-shadow: 0 0.5em 1em rgba(59, 130, 246, 0.35);
    }
}

.box_header_text {
    flex: 1;
    min-width: 0;
}

.box_header_title {
    font-size: 1.5em;
    font-weight: 700;
    color: var(--textPrimary, #1a202c);
    margin: 0 0 0.25em 0;
    line-height: 1.2;
    background: linear-gradient(135deg, var(--textPrimary, #1a202c), var(--textSecondary, #4a5568));
    background-clip: text;
    -webkit-background-clip: text;
    text-transform: none;
}

.box_header_subtitle {
    font-size: 0.875em;
    font-weight: 500;
    color: var(--textSecondary, #6b7280);
    margin: 0;
    line-height: 1.4;
    opacity: 0.8;
    text-transform: none;
}

/* Actions do Header */
.box_header_actions {
    display: flex;
    gap: 0.75em;
    align-items: center;
}

.box_header_action {
    position: relative;
}

.box_header_actionLink {
    text-decoration: none;
    color: inherit;
}

.box_header_actionButton {
    width: 2.5em;
    height: 2.5em;
    border-radius: 0.5em;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(255, 255, 255, 0.8);
    border: 1px solid rgba(0, 0, 0, 0.08);
    cursor: pointer;
    transition: all 0.2s ease;
    font-size: 1em;
    color: var(--textSecondary, #6b7280);
    backdrop-filter: blur(10px);

    &:hover {
        background: var(--colorBlue, #3b82f6);
        color: white;
        transform: translateY(-2px) scale(1.05);
        box-shadow: 0 0.5em 1em rgba(59, 130, 246, 0.25);
        border-color: var(--colorBlue, #3b82f6);
    }

    &:active {
        transform: translateY(0) scale(0.98);
        transition-duration: 0.1s;
    }

    // Botão com texto
    &--withText {
        width: auto;
        padding: 0.5em 1em;
        display: flex;
        align-items: center;
        gap: 0.5em;
        min-height: auto;
        font-size: 0.9em;
        font-weight: 500;
        border-radius: 0.375em;
    }
}

.box_header_actionText {
    font-size: 0.875em;
    font-weight: 500;
    white-space: nowrap;
}

/* Select personalizado para Box */
.box_header_select {
    position: relative;
    min-width: 180px;
}

.box_header_selectButton {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75em;
    padding: 0.625em 1em;
    background: rgba(255, 255, 255, 0.9);
    border: 1px solid rgba(0, 0, 0, 0.08);
    border-radius: 0.5em;
    cursor: pointer;
    transition: all 0.2s ease;
    backdrop-filter: blur(10px);
    min-height: 2.5em;
    box-sizing: border-box;

    &:hover {
        background: rgba(255, 255, 255, 1);
        border-color: var(--colorBlue, #3b82f6);
        box-shadow: 0 2px 8px rgba(59, 130, 246, 0.15);
    }

    &--active {
        background: var(--colorBlue, #3b82f6);
        border-color: var(--colorBlue, #3b82f6);
        color: white;
        box-shadow: 0 2px 8px rgba(59, 130, 246, 0.25);

        .box_header_selectText {
            color: white;
        }

        .box_header_selectIcon {
            color: white;
        }

        .sw-icon {
            color: white;
        }
    }
}

.box_header_selectContent {
    display: flex;
    align-items: center;
    gap: 0.5em;
    flex: 1;
    min-width: 0;
}

.box_header_selectIcon {
    font-size: 1em;
    color: var(--colorBlue, #3b82f6);
    flex-shrink: 0;
    transition: color 0.2s ease;
}

.box_header_selectText {
    font-size: 0.875em;
    font-weight: 500;
    color: var(--textPrimary, #1f2937);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    flex: 1;
}

.rotate-180 {
    transform: rotate(180deg);
}

.box_header_selectDropdown {
    position: absolute;
    top: calc(100% + 0.5em);
    left: 0;
    right: 0;
    background: white;
    border: 1px solid rgba(0, 0, 0, 0.1);
    border-radius: 0.5em;
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
    backdrop-filter: blur(10px);
    z-index: 9999;
    max-height: 200px;
    overflow-y: auto;
    animation: dropdownSlide 0.2s ease-out;
}

.box_header_selectOption {
    display: flex;
    align-items: center;
    gap: 0.75em;
    padding: 0.75em 1em;
    cursor: pointer;
    transition: all 0.2s ease;
    border-bottom: 1px solid rgba(0, 0, 0, 0.05);

    &:last-child {
        border-bottom: none;
    }

    &:hover {
        background: rgba(59, 130, 246, 0.08);
        color: var(--colorBlue, #3b82f6);
    }

    &--selected {
        background: rgba(59, 130, 246, 0.12);
        color: var(--colorBlue, #3b82f6);
        font-weight: 600;

        &::after {
            content: '✓';
            margin-left: auto;
            font-weight: bold;
        }
    }

    span {
        font-size: 0.875em;
        font-weight: 500;
    }

    .sw-icon {
        font-size: 1em;
        width: 1.25em;
        height: 1.25em;
        color: var(--textSecondary, #6b7280);
    }
}

@keyframes dropdownSlide {
    from {
        opacity: 0;
        transform: translateY(-0.5em);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

/* Input personalizado para Box */
.box_header_input {
    min-width: 150px;
}

.box_header_inputField {
    width: 100%;
    padding: 0.625em 1em;
    background: rgba(255, 255, 255, 0.9);
    border: 1px solid rgba(0, 0, 0, 0.08);
    border-radius: 0.5em;
    font-size: 0.875em;
    font-weight: 500;
    color: var(--textPrimary, #1f2937);
    transition: all 0.2s ease;
    backdrop-filter: blur(10px);
    min-height: 2.5em;
    box-sizing: border-box;

    &:focus {
        outline: none;
        background: rgba(255, 255, 255, 1);
        border-color: var(--colorBlue, #3b82f6);
        box-shadow: 0 2px 8px rgba(59, 130, 246, 0.15);
    }

    &::placeholder {
        color: var(--textMuted, #9ca3af);
        opacity: 0.7;
    }
}

/* Toggle/Switch personalizado para Box */
.box_header_toggle {
    display: flex;
    align-items: center;
}

.box_header_toggleLabel {
    display: flex;
    align-items: center;
    gap: 0.5em;
    cursor: pointer;
    user-select: none;
}

.box_header_toggleInput {
    display: none;
}

.box_header_toggleSlider {
    position: relative;
    width: 2.5em;
    height: 1.5em;
    background: rgba(0, 0, 0, 0.2);
    border-radius: 0.75em;
    transition: all 0.3s ease;
    cursor: pointer;

    &::before {
        content: '';
        position: absolute;
        top: 0.125em;
        left: 0.125em;
        width: 1.25em;
        height: 1.25em;
        background: white;
        border-radius: 50%;
        transition: all 0.3s ease;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
    }
}

.box_header_toggleInput:checked + .box_header_toggleSlider {
    background: var(--colorBlue, #3b82f6);

    &::before {
        transform: translateX(1em);
    }
}

.box_header_toggleText {
    font-size: 0.875em;
    font-weight: 500;
    color: var(--textPrimary, #1f2937);
}

/* Grupo de botões */
.box_header_buttonGroup {
    display: flex;
    gap: 0.25em;
    background: rgba(255, 255, 255, 0.9);
    border: 1px solid rgba(0, 0, 0, 0.08);
    border-radius: 0.5em;
    padding: 0.25em;
    backdrop-filter: blur(10px);

    .box_header_actionButton {
        margin: 0;
        border: none;
        border-radius: 0.25em;
        min-width: 2em;
        height: 2em;
        background: transparent;
        transition: all 0.2s ease;

        &:hover {
            background: rgba(59, 130, 246, 0.1);
            transform: none;
            box-shadow: none;
            border-color: transparent;
        }

        &--active {
            background: var(--colorBlue, #3b82f6);
            color: white;

            &:hover {
                background: var(--colorBlueDark, #1d4ed8);
            }
        }
    }
}

/* Content do Box */
.box_content {
    padding: 2em;
    background: var(--backgroundCard, #ffffff);
    position: relative;
    
    &--noHeader {
        border-radius: 0.75em;
        padding-top: 2.5em;
    }
}

/* Loading Overlay */
.box_loading {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(255, 255, 255, 0.9);
    backdrop-filter: blur(4px);
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 0.75em;
    z-index: 10;
    
    .spinner {
        font-size: 2em;
    }
}

/* Responsividade */
@media (max-width: 768px) {
    .box_header {
        padding: 1.5em;
        flex-direction: column;
        gap: 1em;
        text-align: center;
    }

    .box_header_content {
        justify-content: center;
    }

    .box_header_title {
        font-size: 1.25em;
    }

    .box_header_subtitle {
        font-size: 0.8em;
    }

    .box_content {
        padding: 1.5em;
    }
}

@media (max-width: 480px) {
    .box_header_icon {
        width: 2.5em;
        height: 2.5em;
        font-size: 1em;
    }

    .box_header_content {
        gap: 1em;
    }

    .box_header_title {
        font-size: 1.125em;
    }

    .box_content {
        padding: 1.25em;
    }
}

/* Animações */
@keyframes slideIn {
    from {
        opacity: 0;
        transform: translateY(1em);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

.box {
    animation: slideIn 0.5s ease-out;
}

/* Estados especiais */
.box:focus-within {
    outline: none;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1),
                0 0.5em 2em rgba(0, 0, 0, 0.12), 
                0 0.25em 1em rgba(0, 0, 0, 0.08);
}
</style>